/**
 * Survey spatial utilities — pure functions, no React dependencies.
 *
 * @remarks
 * Estimates the geographic position of a marked object from an image click,
 * using camera position and heading. Used in Fase 5.2 (image marking).
 *
 * @module survey/utils/spatial
 */

/** Metres projected forward along cameraHeading from camera position. */
const INITIAL_OFFSET_M = 15;

/** Maximum lateral offset (metres, perpendicular to heading) at image edges. */
const MAX_LATERAL_M = 5;

export interface EstimatedPosition {
  estimatedLat: number;
  estimatedLon: number;
  /** Confidence score [0–1]. 0.5 when heading is known, 0.2 as fallback. */
  confidence: number;
}

/**
 * Estimates the geographic position of a marked object from an image click.
 *
 * Projects INITIAL_OFFSET_M metres forward along cameraHeading and adds a
 * lateral offset (±MAX_LATERAL_M m, perpendicular to heading) proportional
 * to how far imageX is from the horizontal centre of the image.
 *
 * Falls back to camera position with confidence 0.2 when heading is null
 * or naturalWidth is zero.
 */
export function estimatePositionFromImageClick(
  cameraLat: number,
  cameraLon: number,
  cameraHeading: number | null,
  imageX: number,
  naturalWidth: number
): EstimatedPosition {
  if (cameraHeading === null || naturalWidth === 0) {
    return { estimatedLat: cameraLat, estimatedLon: cameraLon, confidence: 0.2 };
  }

  const headingRad = (cameraHeading * Math.PI) / 180;
  // Perpendicular direction (90° clockwise from heading = right side)
  const perpRad = headingRad + Math.PI / 2;

  // Normalise: -1 = left edge, 0 = centre, +1 = right edge
  const lateralRatio = (imageX / naturalWidth - 0.5) * 2;
  const lateralM = lateralRatio * MAX_LATERAL_M;

  // Metres-per-degree conversion
  const cosLat = Math.cos((cameraLat * Math.PI) / 180);

  const latOffset =
    (INITIAL_OFFSET_M / 111_320) * Math.cos(headingRad) + (lateralM / 111_320) * Math.cos(perpRad);

  const lonOffset =
    (INITIAL_OFFSET_M / (111_320 * cosLat)) * Math.sin(headingRad) +
    (lateralM / (111_320 * cosLat)) * Math.sin(perpRad);

  return {
    estimatedLat: cameraLat + latOffset,
    estimatedLon: cameraLon + lonOffset,
    confidence: 0.5,
  };
}
